import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WORKFLOW_QUEUES } from '../mock';
import { fullName, money, pct, dateTime, fmtDob } from '../lib/data';
import { planKeyFor } from '../lib/networkStore';
import { useAuth } from '../lib/auth';
import {
  useEligibilityDetail, useDiscrepancies, useWorkflows, useDenialPatterns, usePlanLearnings, useWorklist,
} from '../api/hooks';
import { useClaimsEnabled } from '../lib/features';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import StatusPill from '../components/StatusPill';
import SummaryPoints from '../components/SummaryPoints';
import NetworkConfidence from '../components/NetworkConfidence';
import PlanLearnings from '../components/PlanLearnings';
import RiskBadge from '../components/RiskBadge';
import Confidence from '../components/Confidence';
import AgentChip from '../components/AgentChip';
import Pipeline from '../components/Pipeline';
import Timeline from '../components/Timeline';
import DiffRow from '../components/DiffRow';
import ReasoningTrace from '../components/ReasoningTrace';
import EmptyState from '../components/EmptyState';
import {
  ArrowLeft, Download, RefreshCw, AlertTriangle, ShieldCheck, ThumbsUp, Pencil,
  GitBranch, ArrowRight, Bot, Cpu, Lock,
} from 'lucide-react';
import { toast } from '../lib/aiToast';

const PIPELINE = (active = false) => [
  { label: 'Ingested', status: 'done' },
  { label: '270 sent', status: 'done' },
  { label: '271 received', status: 'done' },
  { label: 'Parsed', status: 'done' },
  { label: 'Risk scored', status: 'done' },
  { label: 'Routed', status: active ? 'active' : 'done' },
];

export default function VerificationDetail() {
  const { id } = useParams();
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const claimsEnabled = useClaimsEnabled();

  const { data: check } = useEligibilityDetail(id);
  const { data: discrepancies = [] } = useDiscrepancies(practiceId, check?.patientId);
  const { data: workflows = [] } = useWorkflows(practiceId);
  const { data: denialPatterns = [] } = useDenialPatterns(practiceId);
  const planKey = check ? planKeyFor(check.payerId || check.payer?.id, check.planNameReturned) : undefined;
  const { data: learnings = [] } = usePlanLearnings(practiceId, { planKey });
  const { data: worklist = [] } = useWorklist(practiceId);
  const aptId = check ? worklist.find((w) => w.check?.id === check.id)?.id : null;

  // Local optimistic copy of discrepancies so apply/dismiss reflects immediately.
  const [discs, setDiscs] = useState([]);
  useEffect(() => { setDiscs(discrepancies); }, [discrepancies]);

  if (!check) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Link to="/app"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
        </div>
        <EmptyState icon={Cpu} title="Loading verification…" hint="Fetching the 271 detail." />
      </div>
    );
  }

  const pat = check.patient;
  const payer = check.payer;
  const provider = check.provider;
  const risk = check.risk || {};
  const fin = check.financials;
  const active = check.coverage?.active !== false;

  const linkedWf = workflows.filter((w) => w.patientId === check.patientId && w.status === 'awaiting_review');
  const relatedPatterns = denialPatterns.filter(
    (d) => d.payerName === payer?.name?.split(' ')[0] || d.planName === check.planNameReturned
  );

  const applyDisc = (dId) => { setDiscs((p) => p.map((d) => (d.id === dId ? { ...d, status: 'applied' } : d))); toast.success('Applied to practice management (Athena)', { agent: 'Data Sync', confidence: 0.97 }); };
  const dismissDisc = (dId) => setDiscs((p) => p.map((d) => (d.id === dId ? { ...d, status: 'dismissed' } : d)));

  const dedRemaining = (fin.deductibleInd || 0) - (fin.deductibleIndMet || 0);
  const dedPct = fin.deductibleInd ? (fin.deductibleIndMet / fin.deductibleInd) * 100 : 0;
  const parsed = check.parsed271 || null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/app"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">{fullName(pat)}</h1>
            <StatusPill status={active ? 'active' : 'inactive'} />
            <StatusPill status={check.networkStatusResolved} />
            <RiskBadge level={risk.level} score={risk.score} showScore />
          </div>
          <div className="text-xs text-slate-500 mono mt-0.5">{check.id} · {dateTime(check.submittedAt)}</div>
        </div>
        {aptId && <Link to={`/app/appointments/${aptId}`}><Button variant="outline"><Cpu className="h-4 w-4 mr-1.5" />Agent chain</Button></Link>}
        <Button variant="outline" onClick={() => toast.message('Re-submitting 270 to payer…')}><RefreshCw className="h-4 w-4 mr-1.5" />Re-run</Button>
        <Button variant="outline"><Download className="h-4 w-4 mr-1.5" />PDF</Button>
      </div>

      {/* Pipeline */}
      <div className="dh-card px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-600"><Cpu className="h-4 w-4 text-brand-500" />Agent pipeline</div>
          <div className="text-[11px] text-slate-400">completed in 2.4s · {dateTime(check.submittedAt)}</div>
        </div>
        <Pipeline steps={PIPELINE()} />
      </div>

      {/* AI summary + HITL */}
      <div className="ai-surface p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <AgentChip agent="verification" />
            <Confidence value={check.id.endsWith('inv') ? 0.95 : 0.9} label="parse" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-slate-600 h-8" onClick={() => toast.success('Marked correct — fed back to model', { agent: 'Verification' })}><ThumbsUp className="h-3.5 w-3.5 mr-1.5" />Looks right</Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => toast.message('Override editor coming soon')}><Pencil className="h-3.5 w-3.5 mr-1.5" />Override</Button>
          </div>
        </div>
        <SummaryPoints check={check} provider={provider} payer={payer} />
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(risk.reasons || []).map((r, idx) => (
            <span key={idx} className="text-[11px] bg-white/70 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{r}</span>
          ))}
        </div>
        <div className="mt-2"><ReasoningTrace risk={risk} /></div>
        <div className="mt-3 text-[11px] text-slate-400">Sources: 271 response · contracted-plans DB · historical claims patterns</div>
      </div>

      {/* Recommended workflows */}
      {linkedWf.length > 0 && (
        <div className="dh-card p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3"><GitBranch className="h-4 w-4 text-brand-500" />Recommended actions</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {linkedWf.map((w) => {
              const q = WORKFLOW_QUEUES.find((x) => x.key === w.queue);
              return (
                <Link key={w.id} to={aptId ? `/app/appointments/${aptId}` : '/app'} className="dh-inset p-3.5 lift block">
                  <div className="flex items-center justify-between">
                    <span className="pill pill-indigo">{q?.label}</span>
                    {w.netConfidence
                      ? <NetworkConfidence count={w.netConfidence.count} threshold={w.netConfidence.threshold} level={w.netConfidence.level} />
                      : <Confidence value={w.confidence} showLabel={false} />}
                  </div>
                  <div className="text-sm font-medium text-slate-900 mt-2">{w.title}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{w.recommendation}</div>
                  <div className="text-xs text-brand-600 mt-2 flex items-center gap-1 font-medium">Open in workflow <ArrowRight className="h-3 w-3" /></div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* 271 captured in 3 segments — demographics kept separate from analytics */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock className="h-3.5 w-3.5 text-violet-500" />
        Captured from the 271 in three segments — demographics are stored separately from the plan/benefit analytics to avoid PHI contamination.
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Segment 1 — Demographics (private) */}
        <div className="dh-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Demographics</div>
            <span className="pill pill-violet"><Lock className="h-3 w-3" />Private segment</span>
          </div>
          <dl className="space-y-2.5 text-sm">
            {[
              ['Name', fullName(pat)], ['DOB', fmtDob(pat?.dob)],
              ['Member ID', `••••${pat?.memberIdLast4}`], ['Gender', pat?.gender],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 text-right">{v}</dd></div>
            ))}
          </dl>
          <div className="text-[11px] text-slate-400 mt-3">Verified against payer response · not used in the analytical overview.</div>
        </div>

        {/* Segment 2 — Plan-level */}
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Plan-level</div>
          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Payer</dt><dd className="text-slate-900 text-right">{payer?.name}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Plan</dt><dd className="text-slate-900 text-right">{check.planNameReturned}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Plan type</dt><dd className="text-slate-900 text-right">{check.coverage.planType}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Network</dt><dd><StatusPill status={check.networkStatusResolved} /></dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Coverage</dt><dd className="text-slate-900 text-right">{check.coverage.planBegin} → {check.coverage.planEnd}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Provider</dt><dd className="text-slate-900 text-right">Dr. {provider?.lastName} · {provider?.npi}</dd></div>
          </dl>
        </div>

        {/* Segment 3 — Benefit-level */}
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Benefit-level · patient responsibility</div>
          {active ? (
            <div className="grid grid-cols-2 gap-2.5 text-sm">
              <div className="dh-inset p-3"><div className="text-xs text-slate-500">Copay</div><div className="text-xl font-semibold text-slate-900 tnum">{money(fin.copay)}</div></div>
              <div className="dh-inset p-3"><div className="text-xs text-slate-500">Coinsurance</div><div className="text-xl font-semibold text-slate-900 tnum">{pct(fin.coinsurance)}</div></div>
              <div className="dh-inset p-3 col-span-2">
                <div className="text-xs text-slate-500">Deductible (individual)</div>
                <div className="flex items-baseline gap-2 mt-1"><span className="text-xl font-semibold text-slate-900 tnum">{money(fin.deductibleIndMet)}</span><span className="text-xs text-slate-500">/ {money(fin.deductibleInd)} met</span></div>
                <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-brand-500" style={{ width: `${dedPct}%` }} /></div>
                <div className="text-[11px] text-slate-500 mt-1 tnum">{money(dedRemaining)} remaining</div>
              </div>
              <div className="dh-inset p-3 col-span-2"><div className="text-xs text-slate-500">Out-of-pocket max remaining</div><div className="text-xl font-semibold text-slate-900 mt-1 tnum">{money(fin.oopMaxIndRemaining)} / {money(fin.oopMaxInd)}</div></div>
            </div>
          ) : (
            <div className="dh-inset p-4 text-sm text-rose-700 bg-rose-50 border-rose-100">Coverage inactive — no benefits to display. Request updated insurance from the patient.</div>
          )}
        </div>

        {/* Flags + denial patterns */}
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Risk & flags</div>
          <div className="space-y-2">
            {[['Prior auth required', check.flags.priorAuthRequired, 'red'], ['Referral required', check.flags.referralRequired, 'amber'], ['New patient', pat?.isNewPatient, 'amber']].map(([label, on, tone]) => (
              <div key={label} className="flex items-center justify-between dh-inset px-3 py-2">
                <span className="text-sm text-slate-700">{label}</span>
                <span className={`pill ${on ? (tone === 'red' ? 'pill-red' : 'pill-amber') : 'pill-green'}`}>{on ? 'Yes' : 'No'}</span>
              </div>
            ))}
          </div>
          {claimsEnabled && relatedPatterns.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase font-semibold text-slate-500 mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Denial history</div>
              {relatedPatterns.slice(0, 2).map((d) => (
                <div key={d.id} className="text-xs p-2.5 bg-rose-50 border border-rose-100 rounded-lg mb-1.5">
                  <div className="font-medium text-rose-900">{d.frequency} prior denials — {d.denialReason}</div>
                  <div className="text-rose-800 mt-0.5">{d.recommendedAction}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Plan learnings — what we know about this plan, applied to the appointment */}
      <PlanLearnings learnings={learnings} />

      {/* Data discrepancies */}
      {discs.length > 0 && (
        <div className="dh-card p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><RefreshCw className="h-4 w-4 text-brand-500" />Data sync — payer vs. practice record</div>
            <AgentChip agent="sync" size="xs" />
          </div>
          <p className="text-xs text-slate-500 mb-2">The 271 returned values that differ from your PM. Apply payer-verified data to prevent CARC 16 denials.</p>
          <div>
            {discs.map((d) => <DiffRow key={d.id} disc={d} onApply={() => applyDisc(d.id)} onDismiss={() => dismissDisc(d.id)} />)}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dh-card-flush">
        <Tabs defaultValue="benefits">
          <div className="px-5 pt-3 border-b border-slate-100">
            <TabsList>
              <TabsTrigger value="benefits">Benefits</TabsTrigger>
              <TabsTrigger value="raw">Raw 271</TabsTrigger>
              <TabsTrigger value="audit">Audit trail</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="benefits" className="px-5 pb-5 pt-3">
            {parsed ? (
              <div className="space-y-5">
                {/* In/out network cost-share matrix */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-2">Cost-share — in-network vs out-of-network</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-slate-500"><tr><th className="text-left font-medium py-2">Benefit</th><th className="text-right font-medium py-2">In-network</th><th className="text-right font-medium py-2">Out-of-network</th></tr></thead>
                      <tbody className="divide-y divide-slate-100 tnum">
                        <tr><td className="py-2 text-slate-700">Deductible · individual</td><td className="py-2 text-right">{money(parsed.deductible.in.individual)}</td><td className="py-2 text-right text-rose-600">{money(parsed.deductible.out.individual)}</td></tr>
                        <tr><td className="py-2 text-slate-700">Deductible · family</td><td className="py-2 text-right">{money(parsed.deductible.in.family)}</td><td className="py-2 text-right text-rose-600">{money(parsed.deductible.out.family)}</td></tr>
                        <tr><td className="py-2 text-slate-700">Out-of-pocket max · individual</td><td className="py-2 text-right">{money(parsed.oop.in.individual)}</td><td className="py-2 text-right text-rose-600">{money(parsed.oop.out.individual)}</td></tr>
                        <tr><td className="py-2 text-slate-700">Out-of-pocket max · family</td><td className="py-2 text-right">{money(parsed.oop.in.family)}</td><td className="py-2 text-right text-rose-600">{money(parsed.oop.out.family)}</td></tr>
                        <tr><td className="py-2 text-slate-700">Coinsurance</td><td className="py-2 text-right">{pct(parsed.coinsurance.in)}</td><td className="py-2 text-right text-rose-600">{pct(parsed.coinsurance.out)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5">Remaining individual deductible: {money(parsed.deductible.remainingIn.individual)} in-network · nothing met this plan year.</div>
                </div>

                {/* Coverage by service */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-2">Coverage by service</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {parsed.services.map((s) => (
                      <div key={s.code} className="dh-inset p-3 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">{s.label}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {s.limit && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 rounded">{s.limit.quantity} {s.limit.qualifier}/yr</span>}
                            {s.preventiveFree && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 rounded">Preventive $0</span>}
                            {s.authReq && <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-1.5 rounded">Prior auth</span>}
                          </div>
                        </div>
                        {s.active && <StatusPill status="active" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <table className="w-full text-sm mt-2">
                <thead className="text-xs text-slate-500"><tr><th className="text-left font-medium py-2">Benefit</th><th className="text-left font-medium py-2">Service type</th><th className="text-left font-medium py-2">Level</th><th className="text-left font-medium py-2">Network</th><th className="text-right font-medium py-2">Amount</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="py-2">Active coverage</td><td className="py-2">Health Benefit Plan (30)</td><td className="py-2">Individual</td><td className="py-2"><StatusPill status={check.networkStatusResolved} /></td><td className="py-2 text-right">—</td></tr>
                  <tr><td className="py-2">Deductible</td><td className="py-2">Office Visit (98)</td><td className="py-2">Individual</td><td className="py-2"><StatusPill status={check.networkStatusResolved} /></td><td className="py-2 text-right tnum">{money(fin.deductibleInd)}</td></tr>
                  <tr><td className="py-2">Co-payment</td><td className="py-2">Office Visit (98)</td><td className="py-2">Individual</td><td className="py-2"><StatusPill status={check.networkStatusResolved} /></td><td className="py-2 text-right tnum">{money(fin.copay)}</td></tr>
                  <tr><td className="py-2">Co-insurance</td><td className="py-2">Office Visit (98)</td><td className="py-2">Individual</td><td className="py-2"><StatusPill status={check.networkStatusResolved} /></td><td className="py-2 text-right tnum">{pct(fin.coinsurance)}</td></tr>
                  <tr><td className="py-2">Out-of-pocket max</td><td className="py-2">Health Benefit Plan (30)</td><td className="py-2">Individual</td><td className="py-2"><StatusPill status={check.networkStatusResolved} /></td><td className="py-2 text-right tnum">{money(fin.oopMaxInd)}</td></tr>
                </tbody>
              </table>
            )}
          </TabsContent>
          <TabsContent value="raw" className="px-5 pb-5">
            {check.raw271Redacted ? (
              <div className="mt-2">
                <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />PHI redacted via <span className="mono">redact271()</span> — safe to log, store, and analyze.</div>
                <pre className="p-4 bg-slate-900 text-slate-200 text-[11px] rounded-lg mono overflow-x-auto leading-relaxed max-h-[480px]">{JSON.stringify(check.raw271Redacted, null, 2)}</pre>
              </div>
            ) : (
            <pre className="mt-2 p-4 bg-slate-900 text-slate-200 text-xs rounded-lg mono overflow-x-auto leading-relaxed">
{`ISA*00*          *00*          *ZZ*STEDI          *01*${check.stediRequestId}      *250504*0812*^*00501*123456789*0*P*>~
GS*HB*STEDI*${check.stediRequestId}*20260504*0812*1*X*005010X279A1~
ST*271*1001*005010X279A1~
BHT*0022*11*${check.id}*20260504*0812~
HL*1**20*1~NM1*PR*2*${payer?.name?.toUpperCase()}*****FI*${payer?.stediPayerId}~
HL*2*1*21*1~NM1*1P*2*PRACTICE*****XX*${provider?.npi}~
HL*3*2*22*0~NM1*IL*1*${pat?.lastName?.toUpperCase()}*${pat?.firstName?.toUpperCase()}***MI*****${pat?.memberIdLast4}~
DMG*D8*${pat?.dob?.replace(/-/g, '')}*${pat?.gender}~
DTP*346*D8*${check.coverage.planBegin?.replace(/-/g, '')}~
EB*${active ? '1' : '6'}**30**${check.planNameReturned}~
EB*B*IND*98***27*${fin.copay}.00****Y~
EB*C*IND*98***23*${fin.deductibleInd}.00****Y~
EB*A*IND*98****${fin.coinsurance.toFixed(2)}***Y~
SE*15*1001~GE*1*1~IEA*1*123456789~`}
            </pre>
            )}
          </TabsContent>
          <TabsContent value="audit" className="px-5 pb-5 pt-4">
            <Timeline events={[
              { title: 'Submitted by Sarah Lin', ts: dateTime(check.submittedAt), tone: 'indigo' },
              { title: `Stedi 270 routed to ${payer?.name}`, detail: `Request ${check.stediRequestId}`, ts: dateTime(check.submittedAt), tone: 'sky' },
              { title: '271 received and parsed by Verification Agent', detail: 'Structured benefits extracted', ts: dateTime(check.submittedAt), tone: 'violet', icon: Bot },
              { title: 'Plan resolved against contracted-plans DB', detail: `Network: ${check.networkStatusResolved.replace(/_/g, ' ')}`, ts: dateTime(check.submittedAt), tone: 'indigo' },
              { title: `Risk Agent scored ${risk.score} (${risk.level})`, detail: (risk.reasons || []).join(' · '), ts: dateTime(check.submittedAt), tone: risk.level === 'high' ? 'red' : risk.level === 'medium' ? 'amber' : 'green', icon: Bot },
            ]} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
